/**
 * @date: 2022-04-11
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useEffect, useState} from 'react'
import {
    Button,
    Grid,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import {handleHttpResponse} from "../utils/http-helpers";
import {CustomBackdrop} from "../utils/customStyles";
import ShowSnackbar from "../utils/ShowSnackbar";
import {getComparator, sortedRowInformation} from "../Hisps/HispsAsList";
import {useDispatch, useSelector} from "react-redux";
import {getAllHisps} from "../features/allHispsSlice";
import {Link} from "react-router-dom";


const StyledPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    boxShadow: '0 7px 20px #cccccc',
    height: 320,
    gap: 30,
}))
const StyledTableRow = styled(TableRow)(({theme}) => ({
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));
const StyledTableCell = styled(TableCell)(({theme}) => ({
    // [`&.${tableCellClasses.head}`]: {
    //     backgroundColor: theme.palette.primary.main,
    //     color: theme.palette.common.white,
    // },
    // [`&.${tableCellClasses.body}`]: {
    //     fontSize: 14,
    // },
    fontWeight: 'bold'
}))





const length = 3;

const calculateScore = (count, totalCount) => {
    if (!totalCount) {
        return "0.0%";
    }
    return `${((count / totalCount) * 100).toFixed(1)}%`;
}

const calculateOverallScoreValue = (contribution) => {
    if (!contribution.inputRecordCount) {
        return 0;
    }
    return (
        contribution.errorCount +
        contribution.warningCount +
        contribution.alertCount
    ) / contribution.inputRecordCount;
}

export default function PublishedFewestErrorAndScore(props) {

    const [loading, setLoading] = useState(false)
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg: '', msgLv: ''});
    const [sortedByErrors, setSortedByErrors] = useState([])
    const [sortedByScore, setSortedByScore] = useState([])
    const [errorsAndScore, setErrorsAndScore] = useState({});

    const {hispsLogo} = useSelector(state => state.allHisps);
    const dispatch = useDispatch();

    useEffect(() => {
        fetchPage();
        dispatch(getAllHisps());
    }, [])


    const fetchPage = async () => {
        setLoading(true);
        const url = ApiUrl.Published.getPublication.getPath("last", true, true);
        console.log("GET contribution from: " + url);
        try {
            const res = await fetch(url)
            const data = await handleHttpResponse(res);
            calculateErrorAndScore(data.contributions);
        } catch (err) {
            console.error("failed to get contribution page: " + err)
            setSnackbarMsg({open: true, msg: 'Failed to Get Contribution Page', msgLv: 'error'});
        } finally {
            setLoading(false);
        }
    }

    const comparator = (a, b) => {
        const o1 = calculateOverallScoreValue(a);
        const o2 = calculateOverallScoreValue(b);

        if (o2 < o1) {
            return 1;
        } else if (o2 > o1) {
            return -1;
        } else {
            return 0;
        }
    }

    const calculateErrorAndScore = (contributions) => {
        const byScore = sortedRowInformation(contributions, comparator);
        const byErrors = sortedRowInformation(contributions, getComparator('asc', 'errorCount'))

        setSortedByScore(byScore.slice(0, length));
        setSortedByErrors(byErrors.slice(0, length));
    }

    return (
        <Grid container>
            <Grid item xs={12} md={6} lg={6}>
                <b>
                    Top 3 Fewest Error
                    {/*<Button component={Link} to={PortalUrl.Published.Publication.getPath("last")}>View Detail</Button>*/}
                    {/*<Button component={Link} to={`${PortalUrl.Published.Publication.getPath('last')}1`}>View Detail</Button>*/}
                    <Button component={Link} to={PortalUrl.Published.Publication.getPath('last')} state={{ id: 1, type: 'errors' }}>View Detail</Button>
                </b>
                <StyledPaper style={{ marginRight: 10}}>
                    <ErrorAndScoreWidget contributions={sortedByErrors} hispsLogo={hispsLogo} type='Errors' />

                </StyledPaper>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
                <b>
                    Top 3 Lowest Score
                    {/*<Button component={Link} to={`${PortalUrl.Published.Publication.getPath("last")}2`}>View Detail</Button>*/}
                    <Button component={Link} to={PortalUrl.Published.Publication.getPath("last")} state={{ id: 2, type: 'score' }}>View Detail</Button>
                </b>
                <StyledPaper>
                    <ErrorAndScoreWidget contributions={sortedByScore} hispsLogo={hispsLogo} type='Score' />
                </StyledPaper>
            </Grid>
            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg}/>
            <CustomBackdrop open={loading}/>
        </Grid>
    )
}


export function ErrorAndScoreWidget (props) {
    const { hispsLogo, contributions, type } = props;

    if (type === 'Score') {
        return (
            <TableContainer style={{ overflow: 'auto'}}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>No.</StyledTableCell>
                            <StyledTableCell>HISP</StyledTableCell>
                            <TableCell> </TableCell>
                            <StyledTableCell>Tol Err</StyledTableCell>
                            <StyledTableCell>Untol Err</StyledTableCell>
                            <StyledTableCell>Tol Warn</StyledTableCell>
                            <StyledTableCell>Untol Warn</StyledTableCell>
                            <StyledTableCell>Tol Alert</StyledTableCell>
                            <StyledTableCell>Untol Alert</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contributions && hispsLogo && contributions.map((c, index) => {
                            let logo = "";
                            if (hispsLogo.hasOwnProperty(c.hisp)) {
                                logo = hispsLogo[c.hisp]
                            }

                            const untoleratedErrorCount = c.errorCount - c.toleratedErrorCount;
                            const untoleratedWarningCount = c.warningCount - c.toleratedWarningCount;
                            const untoleratedAlertCount = c.alertCount - c.toleratedAlertCount;

                            return (
                                <StyledTableRow key={c.id} sx={{ height: '60px'}}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{c.hisp}</TableCell>
                                    <TableCell>{logo && <img src={logo} alt={c.hisp} style={{ maxWidth: 70, maxHeight: 35, marginTop: '8px'}} />}</TableCell>
                                    <TableCell>{calculateScore(c.toleratedErrorCount, c.inputRecordCount)}</TableCell>
                                    <TableCell>{calculateScore(untoleratedErrorCount, c.inputRecordCount)}</TableCell>
                                    <TableCell>{calculateScore(c.toleratedWarningCount, c.inputRecordCount)}</TableCell>
                                    <TableCell>{calculateScore(untoleratedWarningCount, c.inputRecordCount)}</TableCell>
                                    <TableCell>{calculateScore(c.toleratedAlertCount, c.inputRecordCount)}</TableCell>
                                    <TableCell>{calculateScore(untoleratedAlertCount, c.inputRecordCount)}</TableCell>
                                </StyledTableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    return (
        <TableContainer style={{ overflow: 'hidden'}}>
            <Table >
                <TableHead>
                    <TableRow>
                        <StyledTableCell>No.</StyledTableCell>
                        <StyledTableCell>HISP</StyledTableCell>
                        <TableCell> </TableCell>
                        <StyledTableCell>{type}</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {contributions && hispsLogo && contributions.map((c, index) => {
                        let logo = "";
                        if (hispsLogo.hasOwnProperty(c.hisp)) {
                            logo = hispsLogo[c.hisp]
                        }

                        return (
                            <StyledTableRow key={c.id} sx={{ height: '60px'}}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{c.hisp}</TableCell>
                                <TableCell>{logo && <img src={logo} alt={c.hisp} style={{ maxWidth: 70, maxHeight: 35, marginTop: '8px'}} />}</TableCell>
                                {type === 'Errors' &&
                                    <TableCell>{c.errorCount}</TableCell>
                                }
                            </StyledTableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
